import { useEffect, useRef, useState } from "react";
import { Upload, Trash2, Save } from "lucide-react";
import { api, API, getToken } from "../lib/api";
import { toast } from "sonner";

const FIELDS = [
  { key: "industry", label: "Industry / niche", placeholder: "e.g. brand strategist for coaches" },
  { key: "preferred_style", label: "Preferred style", placeholder: "Editorial, polished, warm" },
  { key: "preferred_settings", label: "Preferred settings", placeholder: "Studio, modern office, european street" },
  { key: "wardrobe_notes", label: "Wardrobe notes", placeholder: "Cream blazer, navy suit, ivory blouse…" },
  { key: "hair_description", label: "Hair description", placeholder: "Shoulder-length brunette, slight wave" },
  { key: "skin_tone", label: "Skin tone", placeholder: "Warm medium" },
  { key: "additional_notes", label: "Anything else", placeholder: "Recurring jewellery, glasses, etc." },
];

export default function BrandProfile() {
  const [profile, setProfile] = useState(Object.fromEntries(FIELDS.map((f) => [f.key, ""])));
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    const [pRes, refRes] = await Promise.all([api.get("/brand-profile"), api.get("/reference-photos")]);
    if (pRes.data.profile) {
      const p = pRes.data.profile;
      setProfile({
        industry: p.industry || "",
        preferred_style: p.preferred_style || "",
        preferred_settings: p.preferred_settings || "",
        wardrobe_notes: p.wardrobe_notes || "",
        hair_description: p.hair_description || "",
        skin_tone: p.skin_tone || "",
        additional_notes: p.additional_notes || "",
      });
    }
    setPhotos(refRes.data.photos || []);
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/brand-profile", profile);
      toast.success("Brand profile saved");
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post("/reference-photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Reference photos uploaded");
      load();
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this reference photo?")) return;
    await api.delete(`/reference-photos/${id}`);
    load();
  };

  return (
    <div className="max-w-5xl mx-auto" data-testid="brand-profile-page">
      <span className="eyebrow">Step 1 & 2</span>
      <h1 className="font-display text-4xl md:text-5xl text-[#1A1F3D] mt-3">Your brand profile</h1>
      <p className="text-[#52525b] mt-3 max-w-2xl text-lg">Upload your reference photos and tell us about you. The more specific, the more your prompts will sound like you.</p>

      <section className="mt-10 bsp-card p-6 md:p-8" data-testid="reference-section">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <span className="eyebrow">Reference photos</span>
            <h2 className="font-display text-2xl text-[#1A1F3D] mt-1">{photos.length} uploaded</h2>
            <p className="text-[#52525b] text-sm mt-1">Aim for 5–10 clear photos showing your face from multiple angles.</p>
          </div>
          <button onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-primary" data-testid="upload-ref-btn">
            <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload photos"}
          </button>
          <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => upload(e.target.files)} data-testid="upload-ref-input" />
        </div>
        {photos.length > 0 && (
          <div className="mt-6 grid grid-cols-3 md:grid-cols-5 gap-3">
            {photos.map((p) => (
              <RefPhoto key={p.id} photo={p} onRemove={() => remove(p.id)} />
            ))}
          </div>
        )}
      </section>

      <section className="mt-8 bsp-card p-6 md:p-8" data-testid="profile-section">
        <span className="eyebrow">Brand profile</span>
        <h2 className="font-display text-2xl text-[#1A1F3D] mt-1">Tell us about you</h2>
        <div className="mt-6 grid md:grid-cols-2 gap-5">
          {FIELDS.map((f) => (
            <div key={f.key} className={f.key === "additional_notes" ? "md:col-span-2" : ""}>
              <label className="text-sm font-medium text-[#1A1F3D]">{f.label}</label>
              {f.key === "additional_notes" ? (
                <textarea
                  className="bsp-input mt-1"
                  rows={4}
                  placeholder={f.placeholder}
                  value={profile[f.key]}
                  onChange={(e) => setProfile({ ...profile, [f.key]: e.target.value })}
                  data-testid={`field-${f.key}`}
                />
              ) : (
                <input
                  className="bsp-input mt-1"
                  placeholder={f.placeholder}
                  value={profile[f.key]}
                  onChange={(e) => setProfile({ ...profile, [f.key]: e.target.value })}
                  data-testid={`field-${f.key}`}
                />
              )}
            </div>
          ))}
        </div>
        <div className="mt-7 flex justify-end">
          <button onClick={save} disabled={saving} className="btn-primary" data-testid="save-profile-btn">
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save brand profile"}
          </button>
        </div>
      </section>
    </div>
  );
}

const RefPhoto = ({ photo, onRemove }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/reference-photos/${photo.id}/file?token=${encodeURIComponent(getToken() || "")}`);
        if (!r.ok) return;
        const b = await r.blob();
        if (cancelled) return;
        url = URL.createObjectURL(b);
        setSrc(url);
      } catch (err) {
        console.error("Failed to load reference photo thumbnail:", err);
      }
    })();
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [photo.id]);

  return (
    <div className="relative group aspect-square rounded-lg overflow-hidden bg-[#F5E6E0]" data-testid={`ref-${photo.id}`}>
      {src && <img src={src} alt={photo.file_name} className="w-full h-full object-cover" />}
      <button
        onClick={onRemove}
        className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-white/90 text-red-600 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};
